import React, { useEffect, useImperativeHandle, useMemo, useReducer, useRef } from 'react';
import { Eye } from '@gravity-ui/icons';
import { Label } from '@gravity-ui/uikit';
import { cn } from '../classname';
import { i18n } from '../i18n/bundle';
import { throttle } from '../lodash';
import { getEditorLineNumberForOffset, scrollToRevealSourceLine, } from '../utils/sync-scroll';
const b = cn('markup-preview');
const SplitModeView = React.forwardRef(({ editor }, ref) => {
    var _a;
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const outerRef = useRef(null);
    const lineElements = useRef([]);
    const shouldScroll = useRef(true);
    const updateLineElements = () => {
        if (!outerRef.current)
            return;
        const els = Array.from(outerRef.current.querySelectorAll('.line'));
        lineElements.current = els
            .filter((el) => { var _a; return el instanceof HTMLElement && ((_a = el.parentElement) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== 0; })
            .map((el) => ({
            el: el,
            line: Number(el.getAttribute('data-line')),
        }));
    };
    const handleEditorScroll = useMemo(() => throttle((instance) => {
        if (!outerRef.current)
            return;
        if (!shouldScroll.current) {
            shouldScroll.current = true;
            return;
        }
        shouldScroll.current = false;
        const line = instance.lineAtHeight(instance.getScrollInfo().top, 'local');
        updateLineElements();
        scrollToRevealSourceLine(Math.min(line === 0 ? 0 : line + 1, instance.lineCount() - 1), lineElements.current, outerRef);
    }, 30), []);
    const handlePreviewScroll = useMemo(() => throttle(() => {
        if (!outerRef.current)
            return;
        if (!shouldScroll.current) {
            shouldScroll.current = true;
            return;
        }
        shouldScroll.current = false;
        updateLineElements();
        let line = getEditorLineNumberForOffset(outerRef.current.scrollTop, lineElements.current, outerRef);
        if (line === null || isNaN(line))
            return;
        line = Math.max(Math.floor(line) - 1, 0);
        const scrollTo = editor.markupEditor.cm.heightAtLine(line, 'local');
        editor.markupEditor.cm.scrollTo(null, scrollTo);
    }, 30), [editor.markupEditor.cm]);
    useEffect(() => {
        const outer = outerRef.current;
        editor.on('change', forceUpdate);
        editor.markupEditor.cm.on('scroll', handleEditorScroll);
        outer === null || outer === void 0 ? void 0 : outer.addEventListener('scroll', handlePreviewScroll);
        editor.markupEditor.cm.refresh();
        return () => {
            editor.off('change', forceUpdate);
            editor.markupEditor.cm.off('scroll', handleEditorScroll);
            outer === null || outer === void 0 ? void 0 : outer.removeEventListener('scroll', handlePreviewScroll);
        };
    }, [editor, handleEditorScroll, handlePreviewScroll]);
    useImperativeHandle(ref, () => outerRef.current);
    return (React.createElement("div", { className: b('outer', {
            vertical: editor.splitMode === 'vertical',
        }), ref: outerRef },
        React.createElement(Label, { icon: React.createElement(Eye, null), className: b('preview-sign'), size: 'm' }, i18n('split-mode-text')), (_a = editor.renderPreview) === null || _a === void 0 ? void 0 :
        _a.call(editor, { getValue: editor.getValue, mode: 'split' })));
});
SplitModeView.displayName = 'SplitModeView';
export { SplitModeView };
